import React, {useState} from 'react';
import {Box, Typography, Tabs, Tab, Tooltip, CircularProgress, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import NationalDashboardShell from '../components/NationalDashboard.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import { useDashboard } from '../../../context/DashboardContext';
import {isSuppressedItem, isSuppressedCell} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';
import {SortToggle} from '../../DashboardCommon/SortToggle.comp';
import {sortBars} from '../../DashboardCommon/barSort';
import {benchmarkText, UNIT} from '../../DashboardCommon/units';

import {BandChip as SharedBandChip} from '../../DashboardCommon/widgets.comp';
const NAVY   = '#052F44';
const ORANGE = '#FF6900';
// Secondary benchmark-marker colour (Global) — kept distinct from NAVY and
// from every risk-level fill colour used on the bars themselves.
const MARKER_SECONDARY = 'var(--brand-global-2, #7B8FC7)';

/* ── Loading / empty states ──────────────────────────────────────── */
const LoadingState = () => <CircularLoader minHeight="300px" />;

const NoDataMessage = () => (
    <NoDataAvailable />
);

/* ── Risk level colour map (matches School Dashboard's RISK_BAND_COLORS) ── */
const RISK_COLOR = {
    // High:   '#E05000',
    // Medium: '#EFA020',
    // Review: '#7B8FC7',
    Monitor:            '#4A9278',
    Watch:               '#EFA020',
    Elevated:            '#E8730C',
    'Priority Concern':  '#E04535',
};
const riskColor = (label) => RISK_COLOR[label] || '#6B7280';

/* ── Band chip (matches School Dashboard's BandChip pill) ─────────── */
const BandChip = ({label, color}) => <SharedBandChip label={label} color={color || riskColor(label)}/>;

/* ── "Use with care" banner (matches School Dashboard's UseWithCareBanner) ── */
const UseWithCareBanner = ({text}) => {
    if (!text) return null;

    const pattern = /(Decision guidance:?\s*Use with care\.?|Use with care\.?)/i;

    if (typeof text === 'string' && pattern.test(text)) {
        const parts = text.split(pattern);
        return (
            <Box sx={{
                mb: 3, p: {xs: 2, md: 2.5}, backgroundColor: 'var(--brand-surface-subtle, #FEE2E2)', border: '1px solid #FECACA',
                borderLeft: '4px solid #DC2626', borderRadius: '0 8px 8px 0',
            }}>
                <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.7}}>
                    {parts.map((part, index) => {
                        if (pattern.test(part)) {
                            return <strong key={index} style={{color: '#DC2626'}}>{part}</strong>;
                        }
                        return part;
                    })}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            mb: 3, p: {xs: 2, md: 2.5}, backgroundColor: 'var(--brand-surface-subtle, #FEE2E2)', border: '1px solid #FECACA',
            borderLeft: '4px solid #DC2626', borderRadius: '0 8px 8px 0',
        }}>
            <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.7}}>
                {text}
            </Typography>
        </Box>
    );
};

/* ════════════════════════════════════════════════════════════════ */
/*  TAB 1 — OVERVIEW                                               */
/* ════════════════════════════════════════════════════════════════ */

const RISK_LEGEND = Object.keys(RISK_COLOR);

// A risk under the n floor (suppressed) shows the placeholder in place of its bar, figure and band.
export const RiskRow = ({id, name, percent, level, benchmark, sensitive, suppressed, groupLabel}) => (
    <Box sx={{
        display: 'grid',
        // Last track must fit "Priority Concern" (the longest band label) at
        // BandChip's font/padding without the pill's fixed-width background
        // shrinking below the (non-wrapping) text and letting it spill out.
        gridTemplateColumns: {xs: '200px 1fr 40px 140px', md: '300px 1fr 40px 140px'},
        alignItems: 'center',
        gap: 2,
        mb: 1.8,
    }}>
        <Typography sx={{fontSize: '13px', color: NAVY, lineHeight: 1.3}}>
            <strong>{id}</strong>&nbsp;&nbsp;{name}
            {sensitive && <Typography component="span" sx={{fontSize: '11px', color: '#9CA3AF', ml: 1}}>· sensitive</Typography>}
            {/* In the value-sorted (flattened) list each row names its risk group. */}
            {groupLabel && <Typography component="span" sx={{display: 'block', fontSize: '11px', color: '#6A7282', mt: 0.25}}>{groupLabel}</Typography>}
        </Typography>
        {suppressed ? <Box sx={{gridColumn: '2 / span 3'}}><SuppressedValue/></Box> : (<>
        <Box sx={{position: 'relative', height: '12px', backgroundColor: 'var(--brand-surface-muted, #F3F4F6)', borderRadius: '6px'}}>
            <Tooltip title={percent === null ? '—' : `${percent}%`} arrow placement="top">
                <Box sx={{position: 'absolute', left: 0, top: 0, bottom: 0, width: `${percent ?? 0}%`, backgroundColor: riskColor(level), borderRadius: '6px', cursor: 'pointer'}}/>
            </Tooltip>
            {benchmark != null && (
                <Tooltip title={benchmarkText('Global avg', benchmark, UNIT.PERCENT)} arrow placement="bottom">
                    <Box sx={{
                        position: 'absolute', left: `${benchmark}%`, bottom: '-9px', width: 0, height: 0,
                        borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
                        borderBottom: `9px solid ${MARKER_SECONDARY}`,
                        transform: 'translateX(-50%)', cursor: 'pointer', zIndex: 4,
                    }}/>
                </Tooltip>
            )}
        </Box>
        <Typography sx={{fontSize: '14px', fontWeight: 800, color: NAVY, textAlign: 'right'}}>{percent === null ? '—' : `${percent}%`}</Typography>
        <Box>{level && <BandChip label={level} color={riskColor(level)}/>}</Box>
        </>)}
    </Box>
);

/* Global avg marker legend — National dashboard has no benchmark above
   itself, so unlike School's version there's no "National avg" marker here. */
const RiskLegend = () => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 3, mb: 2.5, flexWrap: 'wrap'}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: `7px solid ${MARKER_SECONDARY}`}}/>
            <Typography sx={{fontSize: '12px', color: '#6A7282'}}>Global avg</Typography>
        </Box>
    </Box>
);

export const OverviewTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: 'S_5' });
    const [sortMode, setSortMode] = useState('group');   // group order · highest first · lowest first (QA 9 Oct)

    // Defensively locate section S_5
    let s5Section = null;
    if (dashboardData) {
        if (dashboardData.section_code === 'S_5') {
            s5Section = dashboardData;
        } else if (dashboardData.cards?.sections) {
            s5Section = dashboardData.cards.sections.find(s => s.section_code === 'S_5');
        } else if (Array.isArray(dashboardData.sections)) {
            s5Section = dashboardData.sections.find(s => s.section_code === 'S_5');
        } else if (dashboardData.cards && Array.isArray(dashboardData.cards)) {
            s5Section = dashboardData;
        }
    }

    if (loading) {
        return <LoadingState />;
    }
    if (!dashboardData || !s5Section) {
        return <NoDataMessage />;
    }

    const s5Cards = s5Section?.cards || [];
    const headerTitle = "Cyber-Risk Profile";   // static copy (frontend-owned): "Cyber-Risk" as an adjective
    const headerSubtitle = "Which child online risks are most prevalent nationally, and which need action first?";
    const useWithCareText = "Decision guidance: Use with care. Risk prevalence is a status, not a competency score — higher means more children affected. Differences should prompt support, not assumptions. Subgroups under 10 children are suppressed.";

    const barChartCard = s5Cards.find(c => c.card_type === 'BarChartBMCard');

    const items = barChartCard?.items || [];

    // Group by the item's real category (parent_label) — same field
    // getRiskOptionObjects uses elsewhere in this file. Only fall back to
    // splitting the label on ' · ' for items that carry no parent_label at
    // all, since these items' labels have no such separator and previously
    // produced one group per item instead of the canonical 5 risk groups.
    const grouped = {};
    items.forEach((item, index) => {
        let group, name;
        if (item.parent_label) {
            group = item.parent_label.trim();
            name = item.label.trim();
        } else {
            const parts = item.label.split(' · ');
            group = (parts[0] || 'Other').trim();
            name = (parts[1] || item.label).trim();
        }

        if (!grouped[group]) {
            grouped[group] = [];
        }
        
        const globalBenchmark = item.benchmark_values?.find(b => b.name === 'Global avg.')?.value;
        // The API doesn't guarantee canonical order (items may arrive ranked
        // by score) — item.sublabel carries the item's true fixed sequence
        // number, same field used to order the Region/Age/Gender tabs.
        const seqNum = item.sublabel ? parseInt((item.sublabel.match(/\d+/) || [])[0], 10) : (index + 1);
        const id = String(seqNum || index + 1).padStart(2, '0');

        grouped[group].push({
            id,
            seqNum: seqNum || index + 1,
            name,
            suppressed: isSuppressedItem(item),                 // under the n floor: placeholder row
            percent: isSuppressedItem(item) || item.value == null ? null : Math.round(item.value),
            level: isSuppressedItem(item) ? null : item.band,
            benchmark: globalBenchmark ? Math.round(globalBenchmark) : null,
            sensitive: item.code === 'SexualContent' || item.code === 'SexualConvo' || item.code === 'OfflineMeeting' || item.code === 'UnwantedSexContct' || name.toLowerCase().includes('sexual') || name.toLowerCase().includes('offline meeting')
        });
    });

    // Sort items within each group by their fixed sequence number, then sort
    // the groups themselves by their lowest sequence number — since the 5
    // risk groups are contiguous ranges in that canonical sequence, this
    // reconstructs the correct group order too (Conduct → Content → Contact →
    // Contract & Data → Wellbeing & Screen Use).
    const dynamicRiskGroups = Object.keys(grouped)
        .map(groupName => ({
            group: groupName,
            items: [...grouped[groupName]].sort((a, b) => a.seqNum - b.seqNum)
        }))
        .sort((a, b) => a.items[0].seqNum - b.items[0].seqNum);
    // Group order keeps the risk groups and their headings; the value modes rank every risk in one list (suppressed last).
    const shownRiskGroups = sortBars(dynamicRiskGroups, sortMode);

    return (
        <Box>
            <Box sx={{mb: 3}}>
                <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, lineHeight: 1.2}}>
                    {headerTitle}
                </Typography>
                <Typography sx={{fontSize: '14px', fontStyle: 'italic', color: '#4B5563', mt: 0.75}}>
                    {headerSubtitle}
                </Typography>
            </Box>

            <UseWithCareBanner text={useWithCareText}/>

            <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3}}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1}}>
                    <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>
                        {barChartCard?.title || "National Cyber-Risk Profile vs Global averages"}
                    </Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                        <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                            {RISK_LEGEND.map((l) => <BandChip key={l} label={l} color={riskColor(l)}/>)}
                        </Box>
                        <SortToggle value={sortMode} onChange={setSortMode}/>
                    </Box>
                </Box>

                <RiskLegend/>
                {shownRiskGroups.map(({group, items}) => (
                    <Box key={group || 'all'} sx={{mb: 2}}>
                        {group && (
                            <Typography sx={{fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: ORANGE, mt: 1.5, mb: 1}}>
                                {group}
                            </Typography>
                        )}
                        {items.map(item => <RiskRow key={`${item.id} ${item.name}`} {...item}/>)}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

/* ════════════════════════════════════════════════════════════════ */
/*  TAB 2 — BY REGION                                              */
/* ════════════════════════════════════════════════════════════════ */

/* Fallback value → band, used only when a cell has no explicit band from the
   API. Risk is the inverse of readiness — higher value is worse. */
const riskScoreToBand = (v) => {
    const val = v || 0;
    if (val >= 75) return 'Priority Concern';
    if (val >= 50) return 'Elevated';
    if (val >= 25) return 'Watch';
    return 'Monitor';
};

const getBandColor = (band) => {
    if (!band || typeof band !== 'string') return null;
    const lowerBand = band.toLowerCase();
    const matchedKey = Object.keys(RISK_COLOR).find(
        (key) => key.toLowerCase() === lowerBand
    );
    return matchedKey ? RISK_COLOR[matchedKey] : null;
};

const getRiskCellBg = (band, val) => {
    // No band and no value (a cell that is not sent) stays uncoloured rather than reading as "Monitor".
    const color = getBandColor(band) || (val === null || val === undefined ? null : RISK_COLOR[riskScoreToBand(val)]);
    if (!color) return 'transparent';
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    const opacity = color.toUpperCase() === '#E04535' ? 0.92 : 0.8;
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/* ════════════════════════════════════════════════════════════════
   NEW PROPOSED LAYOUT (set aside per request — kept for reference,
   not active). Replaces the table with a "select a risk → bar list
   per region" pattern matching the By Age/By Gender tabs. Restore by
   swapping this block back in for the active ByRegionTab below. ────
const ByRegionTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: 'S_6' });

    // Defensively locate section S_6
    let s6Section = null;
    if (dashboardData) {
        if (dashboardData.section_code === 'S_6') {
            s6Section = dashboardData;
        } else if (dashboardData.cards?.sections) {
            s6Section = dashboardData.cards.sections.find(s => s.section_code === 'S_6');
        } else if (Array.isArray(dashboardData.sections)) {
            s6Section = dashboardData.sections.find(s => s.section_code === 'S_6');
        } else if (dashboardData.cards && Array.isArray(dashboardData.cards)) {
            s6Section = dashboardData;
        }
    }

    // The Region tab's table (S_6) carries no category metadata of its own —
    // its rubric columns are just flat "column_name" strings. The Profile
    // tab's section (S_5) describes the same risk indicators and DOES carry
    // parent_label, so we cross-reference it here to group the risk selector
    // the same way the Profile tab is grouped (Conduct/Content/Contact/... Risk).
    let s5Section = null;
    if (dashboardData) {
        if (dashboardData.section_code === 'S_5') {
            s5Section = dashboardData;
        } else if (dashboardData.cards?.sections) {
            s5Section = dashboardData.cards.sections.find(s => s.section_code === 'S_5');
        } else if (Array.isArray(dashboardData.sections)) {
            s5Section = dashboardData.sections.find(s => s.section_code === 'S_5');
        } else if (dashboardData.cards && Array.isArray(dashboardData.cards)) {
            s5Section = dashboardData;
        }
    }
    const s5BarChartCard = (s5Section?.cards || []).find(c => c.card_type === 'BarChartBMCard');
    const categoryByName = new Map();
    (s5BarChartCard?.items || []).forEach(item => {
        if (!item.parent_label) return;
        let name = item.label || '';
        if (name.includes(' · ')) name = name.split(' · ')[1] || name;
        categoryByName.set(name.trim().toLowerCase(), item.parent_label.trim());
    });

    const s6Cards = s6Section?.cards || [];
    const titleCard = s6Cards.find(c => c.card_type === 'TitleCard');
    const headerTitle = titleCard?.title || "Cyber Risks by Region";
    const headerSubtitle = titleCard?.footer || "Which regions report higher cyber risk exposure and need targeted support?";

    const tableCard = s6Cards.find(c => c.card_type === 'TableRubricCard');
    const leftLineCard = s6Cards.find(c => c.card_code === 'C_2' || c.card_type === 'LeftLineCard');

    // Extract regions
    const stateCol = tableCard?.table_objects?.find(col => col.column_type === 'text');
    const regions = stateCol?.values || [];

    // Extract risk rows — each gets a short "Cyber Risk N" code for
    // selection; the full descriptive name is shown once the risk is
    // selected, instead of cramming every indicator into a wide table.
    const rubricCols = tableCard?.table_objects?.filter(col => col.column_type === 'rubric') || [];
    const riskColumns = rubricCols.map((col, index) => ({
        code: `risk-${index}`,
        label: `Cyber Risk ${index + 1}`,
        name: col.column_name,
        values: col.values || [],
        bands: col.bands || col.cell_data || []
    }));

    const riskOptions = riskColumns.map((col, index) => ({
        code: col.code,
        name: col.name || col.label,
        num: index + 1,
        parent_label: categoryByName.get((col.name || '').trim().toLowerCase())
    }));

    // Hooks must run unconditionally on every render, so this — and the data
    // derivation above it — has to sit above the loading/no-data early return.
    const [selectedCode, setSelectedCode] = React.useState('');
    React.useEffect(() => {
        if (riskColumns.length > 0 && (!selectedCode || !riskColumns.some(c => c.code === selectedCode))) {
            setSelectedCode(riskColumns[0].code);
        }
    }, [riskColumns, selectedCode]);

    if (loading || !dashboardData || !s6Section) {
        return null;
    }

    const selectedCol = riskColumns.find(c => c.code === selectedCode) || riskColumns[0];

    const leftlineHtml = leftLineCard?.leftline_html || '';

    return (
        <Box sx={{maxWidth: '100%', overflow: 'hidden'}}>
            <Box sx={{mb: 3}}>
                <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, lineHeight: 1.2}}>
                    {headerTitle}
                </Typography>
                <Typography sx={{fontSize: '14px', fontStyle: 'italic', color: '#4B5563', mt: 0.75}}>
                    {headerSubtitle}
                </Typography>
            </Box>

            <Grid container spacing={2.5} alignItems="stretch">
                <Grid item xs={12} md={4}>
                    <RiskCategoryList
                        title={tableCard?.title || "Select a Cyber Risk"}
                        options={riskOptions}
                        selectedCode={selectedCode}
                        onSelect={setSelectedCode}
                    />
                </Grid>
                <Grid item xs={12} md={8}>
                    <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%'}}>
                        <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1}}>
                            <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY}}>
                                {selectedCol?.name || selectedCol?.label || ''}
                            </Typography>
                            <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                                {RISK_LEGEND.map((l) => <BandChip key={l} label={l} color={riskColor(l)}/>)}
                            </Box>
                        </Box>

                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2.2}}>
                            {regions.map((region, ri) => {
                                const val = selectedCol?.values?.[ri];
                                const band = selectedCol?.bands?.[ri] || riskScoreToBand(val);
                                return (
                                    <Box key={region} sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                        <Typography sx={{fontSize: '13.5px', fontWeight: 700, color: NAVY, width: 150, flexShrink: 0}}>
                                            {region}
                                        </Typography>
                                        <BandBar pct={val != null ? Math.round(val) : 0} band={band}/>
                                        <Typography sx={{fontSize: '14px', fontWeight: 800, color: NAVY, width: 40, textAlign: 'right', flexShrink: 0}}>
                                            {val != null ? `${Math.round(val)}%` : '-'}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {leftlineHtml ? (
                <Box
                    sx={{
                        mt: 3,
                        border: '1px solid #C7D9E8',
                        borderLeft: `4px solid ${NAVY}`,
                        borderRadius: '8px',
                        backgroundColor: 'var(--brand-surface-subtle, #F0F6FB)',
                        p: {xs: 2, md: 2.5},
                        '& p': { m: 0, fontSize: '13px', color: '#374151', lineHeight: 1.6 },
                        '& strong:nth-of-type(1)': { color: NAVY, fontWeight: 700 },
                        '& strong:nth-of-type(2)': { color: ORANGE, fontWeight: 700 }
                    }}
                    dangerouslySetInnerHTML={{ __html: leftlineHtml }}
                />
            ) : (
                <Box sx={{mt: 3, border: '1px solid #C7D9E8', borderLeft: `4px solid ${NAVY}`, borderRadius: '8px', backgroundColor: 'var(--brand-surface-subtle, #F0F6FB)', p: {xs: 2, md: 2.5}}}>
                    <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, mb: 0.5}}>
                        Decision Guidance:
                    </Typography>
                    <Typography sx={{fontSize: '13px', fontWeight: 700, color: ORANGE, mb: 1.2}}>
                        Use with care
                    </Typography>
                    <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6, mb: 1}}>
                        Differences should prompt support, not assumptions about any region.
                    </Typography>
                    <Typography sx={{fontSize: '13px', fontWeight: 600, color: NAVY, lineHeight: 1.6}}>
                        Prioritize regions where high risk overlaps with weak competency level and support context.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};
════════════════════════════════════════════════════════════════ */

// cell_data carries {band, n, suppressed}; a region under the n floor shows the placeholder, uncoloured.
const riskCellOf = (col, ri) => {
    const cd = col.bands?.[ri];
    const sup = !!cd && typeof cd === 'object' && isSuppressedCell(cd);
    const band = typeof cd === 'string' ? cd : cd?.band;
    return {sup, band, val: sup ? null : col.values[ri]};
};

// Percentage-based columns so the table fills the full container width
// (no dead space with just 1-2 regions): the first column gets a fixed
// 30%, and the remaining 70% is split evenly across however many region
// columns exist. A minWidth floor on region columns keeps them legible —
// if there are enough regions to hit that floor, the table overflows and
// the container scrolls horizontally instead of squeezing columns
// unreadably thin.
const FIRST_COL_PCT = 30;
const regionHeadCell = {py: 1.75, px: 2, backgroundColor: 'var(--brand-surface-subtle, #F9FAFB)', color: NAVY, fontSize: '13px', fontWeight: 700, border: '1px solid #D1D5DB'};

// The heat-map sits in its own component so the sort/filter hook runs below the tab's loading
// branches (QA 9 Oct 2026). Rows are the risks, columns the regions: clicking a region's header
// ranks the risks by that region; a percent filter per region. A cell under the n floor is null,
// so it sorts last and drops out of a range filter.
const RegionalRiskHeatmapTable = ({regions, riskColumns}) => {
    const regionColPct = regions.length > 0 ? (100 - FIRST_COL_PCT) / regions.length : (100 - FIRST_COL_PCT);
    const rows = React.useMemo(() => riskColumns.map((col, idx) => ({...col, idx})), [riskColumns]);
    const columns = React.useMemo(() => [
        {key: 'risk', label: 'Cyber Risk', type: 'text', accessor: (r) => r.name || r.label},
        ...regions.map((region, ri) => ({key: `region-${ri}`, label: region, type: 'percent', accessor: (r) => riskCellOf(r, ri).val})),
    ], [regions]);
    const t = useTableControls(rows, columns);
    return (
        <>
            {(t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t}/>}
            <TableContainer sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflowX: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.04)'}}>
                <Table sx={{width: '100%', minWidth: 560, borderCollapse: 'collapse', tableLayout: 'fixed'}}>
                    <TableHead>
                        <TableRow>
                            <SortFilterHeaderCell {...t.headerProps('risk')} sx={{...regionHeadCell, textAlign: 'left', width: `${FIRST_COL_PCT}%`}}/>
                            {regions.map((region, ri) => (
                                // Region names may be long and the layout is fixed: let the label wrap inside the cell.
                                <SortFilterHeaderCell key={region} {...t.headerProps(`region-${ri}`)} sx={{...regionHeadCell, textAlign: 'center', width: `${regionColPct}%`, minWidth: 130, '& span': {whiteSpace: 'normal'}}}/>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length > 0 && t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={1 + regions.length} sx={{textAlign: 'center', py: 3, fontSize: '13px', color: '#6A7282', border: '1px solid #D1D5DB'}}>No risk matches the filters.</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map((col) => {
                            const nameLower = (col.name || col.label || '').toLowerCase();
                            const isAnyCyberRisk = nameLower.includes('any cyber risk') || nameLower.includes('composite') || col.idx === 0;
                            return (
                                <TableRow key={col.name || col.label}>
                                    <TableCell sx={{textAlign: 'left', py: 1.6, px: 2, backgroundColor: isAnyCyberRisk ? NAVY : '#1A445C', color: '#fff', fontSize: '13px', fontWeight: 700, border: '1px solid #D1D5DB', width: `${FIRST_COL_PCT}%`}}>
                                        {col.name || col.label}
                                    </TableCell>
                                    {regions.map((region, ri) => {
                                        const {sup, band, val} = riskCellOf(col, ri);
                                        return (
                                            <TableCell key={region} sx={{textAlign: 'center', py: 1.6, px: 2, fontSize: '14px', fontWeight: isAnyCyberRisk ? 800 : 700, color: NAVY, border: '1px solid #D1D5DB', backgroundColor: sup ? 'transparent' : getRiskCellBg(band, val), width: `${regionColPct}%`, minWidth: 130}}>
                                                {sup ? <SuppressedValue variant="cell"/> : (val != null ? `${Math.round(val)}%` : '—')}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

export const ByRegionTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: 'S_6' });

    // Defensively locate section S_6
    let s6Section = null;
    if (dashboardData) {
        if (dashboardData.section_code === 'S_6') {
            s6Section = dashboardData;
        } else if (dashboardData.cards?.sections) {
            s6Section = dashboardData.cards.sections.find(s => s.section_code === 'S_6');
        } else if (Array.isArray(dashboardData.sections)) {
            s6Section = dashboardData.sections.find(s => s.section_code === 'S_6');
        } else if (dashboardData.cards && Array.isArray(dashboardData.cards)) {
            s6Section = dashboardData;
        }
    }

    if (loading) {
        return <LoadingState />;
    }
    if (!dashboardData || !s6Section) {
        return <NoDataMessage />;
    }

    const s6Cards = s6Section?.cards || [];
    const headerTitle = "Cyber Risk by Region";
    const headerSubtitle = "Which regions report higher cyber risk exposure and need targeted support?";

    const tableCard = s6Cards.find(c => c.card_type === 'TableRubricCard');

    // Extract regions
    const stateCol = tableCard?.table_objects?.find(col => col.column_type === 'text');
    const regions = stateCol?.values || [];

    // Extract risk rows — column headers use a short "Risk N" label (like
    // the Readiness by Region heatmap), the full descriptive name goes in
    // the legend below the table instead of wrapping across the header.
    const rubricCols = tableCard?.table_objects?.filter(col => col.column_type === 'rubric') || [];
    const riskColumns = rubricCols.map((col, index) => ({
        label: `Cyber Risk ${index + 1}`,
        name: col.column_name,
        values: col.values || [],
        bands: col.bands || col.cell_data || []
    }));

    return (
        <Box sx={{maxWidth: '100%', overflow: 'hidden'}}>
            <Box sx={{mb: 3}}>
                <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, lineHeight: 1.2}}>
                    {headerTitle}
                </Typography>
                <Typography sx={{fontSize: '14px', fontStyle: 'italic', color: '#4B5563', mt: 0.75}}>
                    {headerSubtitle}
                </Typography>
            </Box>

            <Box sx={{maxWidth: '100%', overflow: 'hidden'}}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1}}>
                    <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>
                        {tableCard?.title || "Regional Risk Heatmap"}
                    </Typography>
                    <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                        {RISK_LEGEND.map((l) => <BandChip key={l} label={l} color={riskColor(l)}/>)}
                    </Box>
                </Box>

                <RegionalRiskHeatmapTable regions={regions} riskColumns={riskColumns}/>
            </Box>

            <Box sx={{mt: 3, border: '1px solid #C7D9E8', borderLeft: `4px solid ${NAVY}`, borderRadius: '8px', backgroundColor: 'var(--brand-surface-subtle, #F0F6FB)', p: {xs: 2, md: 2.5}}}>
                <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, mb: 0.5}}>
                    Decision guidance:
                </Typography>
                <Typography sx={{fontSize: '13px', fontWeight: 700, color: ORANGE, mb: 1.2}}>
                    Use with care
                </Typography>
                <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6, mb: 1}}>
                    Differences should prompt support, not assumptions about any region.
                </Typography>
                <Typography sx={{fontSize: '13px', fontWeight: 600, color: NAVY, lineHeight: 1.6}}>
                    Prioritize regions where high risk overlaps with weak competency level and support context.
                </Typography>
            </Box>
        </Box>
    );
};

/* ════════════════════════════════════════════════════════════════ */
/*  TAB 3 — BY AGE / TAB 4 — BY GENDER (shared building blocks)   */
/* ════════════════════════════════════════════════════════════════ */

/* ── Single-value banded bar row ──────────────────────────────────
   Matches the Cyber Risk by Age/Gender slide layout: one bar per
   group, coloured by its Monitor/Watch/Elevated/Priority Concern band. */
const BandBar = ({pct, band, benchmark, height = 18}) => (
    <Box sx={{position: 'relative', flex: 1, height: `${height}px`, backgroundColor: 'var(--brand-surface-muted, #F3F4F6)', borderRadius: `${height / 2}px`}}>
        <Tooltip title={`${pct}%`} placement="top" arrow>
            <Box sx={{
                position: 'absolute', left: 0, top: 0, bottom: 0,
                width: `${pct}%`,
                backgroundColor: riskColor(band),
                borderRadius: `${height / 2}px`,
                transition: 'width 0.4s ease',
            }}/>
        </Tooltip>
        {benchmark != null && (
            <Tooltip title={`Global avg: ${benchmark}%`} arrow placement="bottom">
                <Box sx={{
                    position: 'absolute', left: `${benchmark}%`, bottom: '-9px', width: 0, height: 0,
                    borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
                    borderBottom: `9px solid ${MARKER_SECONDARY}`,
                    transform: 'translateX(-50%)', cursor: 'pointer', zIndex: 4,
                }}/>
            </Tooltip>
        )}
    </Box>
);

const matchesOption = (item, option) => {
    if (!item || !option) return false;
    const labelLower = item.label?.toLowerCase() || '';
    const optionNameLower = option.name?.toLowerCase() || '';
    const optionCodeLower = option.code?.toLowerCase() || '';

    if (item.code && option.code && item.code.toLowerCase() === optionCodeLower) {
        if (!optionNameLower || labelLower === optionNameLower || labelLower.startsWith(optionNameLower + ' ·') || labelLower.startsWith(optionNameLower + '·') || labelLower.startsWith(optionNameLower + ' -')) {
            return true;
        }
    }

    if (optionNameLower) {
        if (labelLower === optionNameLower ||
            labelLower.startsWith(optionNameLower + ' ·') ||
            labelLower.startsWith(optionNameLower + '·') ||
            labelLower.startsWith(optionNameLower + ' -') ||
            labelLower.startsWith(optionNameLower + '-')) {
            return true;
        }
        const parts = item.label?.split(/[·•-]/);
        if (parts && parts[0].trim().toLowerCase() === optionNameLower) {
            return true;
        }
    }
    return false;
};

const getRiskOptionObjects = (listCard, barChartCard) => {
    const map = new Map();

    const registerRisk = (code, name, parent_label, sublabel) => {
        const cleanName = (name || code || '').trim();
        if (!cleanName) return;
        const cleanCode = (code || cleanName).trim();
        const key = cleanCode.toLowerCase() + '::' + cleanName.toLowerCase();
        if (!map.has(key)) {
            map.set(key, { code: cleanCode, name: cleanName, parent_label, sublabel });
        } else if (parent_label || sublabel) {
            const existing = map.get(key);
            if (!existing.parent_label && parent_label) existing.parent_label = parent_label;
            if (!existing.sublabel && sublabel) existing.sublabel = sublabel;
        }
    };

    if (listCard?.list_objects?.length > 0) {
        listCard.list_objects.forEach(obj => {
            registerRisk(obj.code, obj.name || obj.label, obj.parent_label, obj.sublabel);
        });
    }

    if (barChartCard?.items?.length > 0) {
        barChartCard.items.forEach(item => {
            let riskName = item.label || '';
            if (riskName.includes(' · ')) {
                riskName = riskName.split(' · ')[0];
            } else if (riskName.includes('·')) {
                riskName = riskName.split('·')[0];
            } else if (riskName.includes(' - ')) {
                riskName = riskName.split(' - ')[0];
            }
            registerRisk(item.code, riskName, item.parent_label, item.sublabel);
        });
    }

    return Array.from(map.values());
};

/* ── Grouped risk-selector list (matches School Dashboard's RiskSelector) ── */
const groupRiskOptions = (options) => {
    if (!options || options.length === 0) return [];
    const groups = [];
    let currentCategory = null;
    options.forEach((opt, index) => {
        const rawCategory = opt.parent_label || opt.category;
        const category = (rawCategory && rawCategory.trim().toLowerCase() !== 'headline') ? rawCategory.trim() : null;
        if (groups.length === 0 || category !== currentCategory) {
            groups.push({category, items: []});
            currentCategory = category;
        }
        groups[groups.length - 1].items.push({...opt, num: index + 1});
    });
    return groups;
};

const RiskCategoryList = ({title, options, selectedCode, onSelect, hasDataMap}) => {
    const groups = groupRiskOptions(options);
    return (
        <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 2.5, height: '100%'}}>
            <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 1.5}}>{title}</Typography>
            <Box sx={{display: 'flex', flexDirection: 'column'}}>
                {groups.map((grp, gi) => (
                    <Box key={grp.category || `g-${gi}`}>
                        {grp.category && (
                            <Typography sx={{fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: ORANGE, mt: 1.5, mb: 0.5, px: 0.75}}>
                                {grp.category}
                            </Typography>
                        )}
                        {grp.items.map((item) => {
                            const active = item.code === selectedCode;
                            const hasData = hasDataMap ? hasDataMap[item.code] : true;
                            const num = String(item.num).padStart(2, '0');
                            return (
                                <Box
                                    key={item.code}
                                    onClick={() => hasData && onSelect(item.code)}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 0.75,
                                        borderRadius: '8px',
                                        cursor: hasData ? 'pointer' : 'not-allowed',
                                        opacity: hasData ? 1 : 0.5,
                                        backgroundColor: active ? 'rgba(255,105,0,0.08)' : 'transparent',
                                        '&:hover': {
                                            backgroundColor: (active && hasData) ? 'rgba(255,105,0,0.08)' : (hasData ? 'var(--brand-surface-subtle, #F9FAFB)' : 'transparent')
                                        },
                                    }}
                                >
                                    <Typography sx={{fontSize: '13px', fontWeight: active ? 700 : 500, color: active ? NAVY : '#9CA3AF', minWidth: 20, flexShrink: 0}}>
                                        {num}
                                    </Typography>
                                    <Typography sx={{fontSize: '13px', fontWeight: active ? 700 : 500, color: active ? NAVY : '#4B5563'}}>
                                        {item.name}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export const ByAgeTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: 'S_7' });

    // Defensively locate section S_7
    let s7Section = null;
    if (dashboardData) {
        if (dashboardData.section_code === 'S_7') {
            s7Section = dashboardData;
        } else if (dashboardData.cards?.sections) {
            s7Section = dashboardData.cards.sections.find(s => s.section_code === 'S_7');
        } else if (Array.isArray(dashboardData.sections)) {
            s7Section = dashboardData.sections.find(s => s.section_code === 'S_7');
        } else if (dashboardData.cards && Array.isArray(dashboardData.cards)) {
            s7Section = dashboardData;
        }
    }

    const s7Cards = s7Section?.cards || [];
    const headerTitle = "Cyber Risk by Age";
    const headerSubtitle = "Do reported risks differ across age bands, so support can be age-appropriate?";
    const useWithCareText = "Decision guidance: Use with care. Risk prevalence is a status, not a competency score — higher means more children affected. Differences guide age-appropriate support, not assumptions.";

    const listCard = s7Cards.find(c => c.card_type === 'ListCard');
    const barChartCard = s7Cards.find(c => c.card_type === 'BarChartBMCard' || c.card_type === 'BarChartCard' || c.card_code === 'C_4');

    const items = barChartCard?.items || [];
    const riskOptionObjects = React.useMemo(() => getRiskOptionObjects(listCard, barChartCard), [listCard, barChartCard]);

    const hasDataMap = React.useMemo(() => {
        const map = {};
        riskOptionObjects.forEach(opt => {
            map[opt.code] = items.some(item => matchesOption(item, opt));
        });
        return map;
    }, [riskOptionObjects, items]);

    const [selectedCode, setSelectedCode] = React.useState('');

    React.useEffect(() => {
        const enabledOptions = riskOptionObjects.filter(opt => hasDataMap[opt.code]);
        if (enabledOptions.length > 0) {
            if (!selectedCode || !enabledOptions.some(o => o.code === selectedCode)) {
                setSelectedCode(enabledOptions[0].code);
            }
        } else if (riskOptionObjects.length > 0 && !selectedCode) {
            setSelectedCode(riskOptionObjects[0].code);
        }
    }, [riskOptionObjects, hasDataMap, selectedCode]);

    if (loading) {
        return <LoadingState />;
    }
    if (!dashboardData || !s7Section) {
        return <NoDataMessage />;
    }

    const selectedOption = riskOptionObjects.find(obj => obj.code === selectedCode) || riskOptionObjects[0];
    const chartTitle = selectedOption ? selectedOption.name : '';
    const filteredItems = selectedOption ? items.filter(item => matchesOption(item, selectedOption)) : [];

    const getAgeLabel = (row) => {
        let ageVal = row.dims?.age || row.dims?.Age;
        if (!ageVal && row.label) {
            const parts = row.label.split(/[·•-]/);
            if (parts.length > 1) {
                ageVal = parts[parts.length - 1].trim();
            }
        }
        if (!ageVal) return row.label;
        return ageVal.toLowerCase().includes('age') ? ageVal : `Age ${ageVal}`;
    };

    return (
        <Box>
            <Box sx={{mb: 3}}>
                <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, lineHeight: 1.2}}>
                    {headerTitle}
                </Typography>
                <Typography sx={{fontSize: '14px', fontStyle: 'italic', color: '#4B5563', mt: 0.75}}>
                    {headerSubtitle}
                </Typography>
            </Box>

            <UseWithCareBanner text={useWithCareText}/>

            <Grid container spacing={2.5} alignItems="stretch">
                <Grid item xs={12} md={4}>
                    <RiskCategoryList
                        title={listCard?.title || "Select a Cyber Risk"}
                        options={riskOptionObjects}
                        selectedCode={selectedCode}
                        onSelect={setSelectedCode}
                        hasDataMap={hasDataMap}
                    />
                </Grid>
                <Grid item xs={12} md={8}>
                    <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%'}}>
                        <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1}}>
                            <Box>
                                <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY}}>
                                    {chartTitle}
                                </Typography>
                                <Typography sx={{fontSize: '12px', color: '#6A7282', mt: 0.3}}>
                                    Percentage of students who experienced cyber risk in the past year
                                </Typography>
                            </Box>
                            <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                                {RISK_LEGEND.map((l) => <BandChip key={l} label={l} color={riskColor(l)}/>)}
                            </Box>
                        </Box>

                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2.2}}>
                            {filteredItems.map((row, idx) => {
                                // An age band under the n floor arrives flagged: its label stays, its figure is withheld.
                                if (isSuppressedItem(row)) {
                                    return <SuppressedValue key={row.dims?.age || row.label || idx} label={getAgeLabel(row)}/>;
                                }
                                const pct = row.value == null ? null : Math.round(row.value);
                                return (
                                    <Box key={row.dims?.age || row.label || idx} sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                        <Typography sx={{fontSize: '13.5px', fontWeight: 700, color: NAVY, width: 64, flexShrink: 0}}>
                                            {getAgeLabel(row)}
                                        </Typography>
                                        <BandBar pct={pct ?? 0} band={row.band}/>
                                        <Typography sx={{fontSize: '14px', fontWeight: 800, color: NAVY, width: 40, textAlign: 'right', flexShrink: 0}}>
                                            {pct === null ? '—' : `${pct}%`}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export const ByGenderTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: 'S_8' });

    // Defensively locate section S_8
    let s8Section = null;
    if (dashboardData) {
        if (dashboardData.section_code === 'S_8') {
            s8Section = dashboardData;
        } else if (dashboardData.cards?.sections) {
            s8Section = dashboardData.cards.sections.find(s => s.section_code === 'S_8');
        } else if (Array.isArray(dashboardData.sections)) {
            s8Section = dashboardData.sections.find(s => s.section_code === 'S_8');
        } else if (dashboardData.cards && Array.isArray(dashboardData.cards)) {
            s8Section = dashboardData;
        }
    }

    const s8Cards = s8Section?.cards || [];
    const headerTitle = "Cyber Risk by Gender";
    const headerSubtitle = "Do reported risks differ across gender groups, so support can be inclusive?";
    const useWithCareText = "Decision guidance: Use with care. Risk prevalence is a status, not a competency score — higher means more children affected. Respond to the whole cohort rather than targeting any one group, and keep all communications supportive and inclusive.";

    const listCard = s8Cards.find(c => c.card_type === 'ListCard');
    const barChartCard = s8Cards.find(c => c.card_type === 'BarChartBMCard' || c.card_type === 'BarChartCard' || c.card_code === 'C_4' || c.card_code === 'C_5');

    const items = barChartCard?.items || [];
    const riskOptionObjects = React.useMemo(() => getRiskOptionObjects(listCard, barChartCard), [listCard, barChartCard]);

    const hasDataMap = React.useMemo(() => {
        const map = {};
        riskOptionObjects.forEach(opt => {
            map[opt.code] = items.some(item => matchesOption(item, opt));
        });
        return map;
    }, [riskOptionObjects, items]);

    const [selectedCode, setSelectedCode] = React.useState('');

    React.useEffect(() => {
        const enabledOptions = riskOptionObjects.filter(opt => hasDataMap[opt.code]);
        if (enabledOptions.length > 0) {
            if (!selectedCode || !enabledOptions.some(o => o.code === selectedCode)) {
                setSelectedCode(enabledOptions[0].code);
            }
        } else if (riskOptionObjects.length > 0 && !selectedCode) {
            setSelectedCode(riskOptionObjects[0].code);
        }
    }, [riskOptionObjects, hasDataMap, selectedCode]);

    if (loading) {
        return <LoadingState />;
    }
    if (!dashboardData || !s8Section) {
        return <NoDataMessage />;
    }

    const selectedOption = riskOptionObjects.find(obj => obj.code === selectedCode) || riskOptionObjects[0];
    const chartTitle = selectedOption ? selectedOption.name : '';
    const filteredItems = selectedOption ? items.filter(item => matchesOption(item, selectedOption)) : [];

    const genderMap = {
        female: 'Female',
        male: 'Male',
        other: 'Prefer not to say',
        prefer_not_to_say: 'Prefer not to say'
    };

    const getGenderLabel = (row) => {
        let rawGender = row.dims?.gender || row.dims?.Gender;
        if (!rawGender && row.label) {
            const parts = row.label.split(/[·•-]/);
            if (parts.length > 1) {
                rawGender = parts[parts.length - 1].trim();
            }
        }
        if (!rawGender) return row.label;
        return genderMap[rawGender.toLowerCase()] || (rawGender.charAt(0).toUpperCase() + rawGender.slice(1));
    };

    // Fixed display order: Male, Female, then any other/unrecognized groups.
    const genderRank = {Male: 0, Female: 1};
    const sortedItems = [...filteredItems].sort((a, b) => {
        const rankA = genderRank[getGenderLabel(a)] ?? 2;
        const rankB = genderRank[getGenderLabel(b)] ?? 2;
        return rankA - rankB;
    });

    return (
        <Box>
            <Box sx={{mb: 3}}>
                <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, lineHeight: 1.2}}>
                    {headerTitle}
                </Typography>
                <Typography sx={{fontSize: '14px', fontStyle: 'italic', color: '#4B5563', mt: 0.75}}>
                    {headerSubtitle}
                </Typography>
            </Box>

            <UseWithCareBanner text={useWithCareText}/>

            <Grid container spacing={2.5} alignItems="stretch">
                <Grid item xs={12} md={4}>
                    <RiskCategoryList
                        title={listCard?.title || "Select a Cyber Risk"}
                        options={riskOptionObjects}
                        selectedCode={selectedCode}
                        onSelect={setSelectedCode}
                        hasDataMap={hasDataMap}
                    />
                </Grid>
                <Grid item xs={12} md={8}>
                    <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%'}}>
                        <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1}}>
                            <Box>
                                <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY}}>
                                    {chartTitle}
                                </Typography>
                                <Typography sx={{fontSize: '12px', color: '#6A7282', mt: 0.3}}>
                                    Percentage of students who experienced cyber risk in the past year
                                </Typography>
                            </Box>
                            <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                                {RISK_LEGEND.map((l) => <BandChip key={l} label={l} color={riskColor(l)}/>)}
                            </Box>
                        </Box>

                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2.2}}>
                            {sortedItems.map((row, idx) => {
                                // A gender group under the n floor arrives flagged: its label stays, its figure is withheld.
                                if (isSuppressedItem(row)) {
                                    return <SuppressedValue key={row.dims?.gender || row.label || idx} label={getGenderLabel(row)}/>;
                                }
                                const pct = row.value == null ? null : Math.round(row.value);
                                return (
                                    <Box key={row.dims?.gender || row.label || idx} sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                        <Typography sx={{fontSize: '13.5px', fontWeight: 700, color: NAVY, width: 130, flexShrink: 0}}>
                                            {getGenderLabel(row)}
                                        </Typography>
                                        <BandBar pct={pct ?? 0} band={row.band}/>
                                        <Typography sx={{fontSize: '14px', fontWeight: 800, color: NAVY, width: 40, textAlign: 'right', flexShrink: 0}}>
                                            {pct === null ? '—' : `${pct}%`}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

/* ════════════════════════════════════════════════════════════════ */
/*  MAIN COMPONENT                                                  */
/* ════════════════════════════════════════════════════════════════ */

const CyberRisksSection = () => {
    const [activeTab, setActiveTab] = useState(0);

    return (
        <NationalDashboardShell
            tabs={
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    TabIndicatorProps={{style: {backgroundColor: ORANGE}}}
                    sx={{
                        borderBottom: '1px solid #E5E7EB',
                        mb: 1,
                        '& .MuiTab-root': {textTransform: 'none', fontWeight: 600, fontSize: '14px', color: '#6A7282', px: 2},
                        '& .Mui-selected': {color: `${ORANGE} !important`},
                    }}
                >
                    <Tab label="Profile"/>
                    <Tab label="By Region"/>
                    <Tab label="By Age"/>
                    <Tab label="By Gender"/>
                </Tabs>
            }
        >
            {activeTab === 0 && <OverviewTab/>}
            {activeTab === 1 && <ByRegionTab/>}
            {activeTab === 2 && <ByAgeTab/>}
            {activeTab === 3 && <ByGenderTab/>}
        </NationalDashboardShell>
    );
};

export default CyberRisksSection;
